import { useState, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((messageOrOptions, options = {}) => {
    // Canonical signature: confirm(message: string, options).
    // Defensive: also accept confirm({ title, message|description, confirmLabel, ... })
    // and coerce any non-string message so an object can NEVER crash-render
    // as a React child (guards against the "Objects are not valid as a React
    // child" class of bug recurring at any call site).
    let message = messageOrOptions;
    let opts = options;
    if (messageOrOptions && typeof messageOrOptions === 'object') {
      opts = messageOrOptions;
      message = messageOrOptions.message ?? messageOrOptions.description ?? '';
    }
    return new Promise((resolve) => {
      setState({
        message: typeof message === 'string' ? message : String(message ?? ''),
        title: opts.title || 'Are you sure?',
        confirmLabel: opts.confirmLabel || 'Delete',
        cancelLabel: opts.cancelLabel || 'Cancel',
        destructive: opts.destructive !== false,
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => { state?.resolve(true); setState(null); };
  const handleCancel = () => { state?.resolve(false); setState(null); };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && createPortal(
        <div className="ug-modal fixed inset-0 z-[99998] flex items-center justify-center p-4" onClick={handleCancel} data-testid="confirm-dialog">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-ug-raised border border-ug-border rounded-2xl shadow-ug-lg w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              {state.destructive && (
                <div className="w-10 h-10 rounded-xl bg-ug-status-red/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-ug-status-red" />
                </div>
              )}
              <div>
                <h3 data-role="section-title" className="text-zinc-100 font-sans font-semibold text-base">{state.title}</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{state.message}</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={handleCancel}
                className="px-4 py-2 rounded-xl text-sm font-sans font-medium text-zinc-400 hover:text-zinc-200 border border-ug-border hover:border-ug-border-strong transition-colors whitespace-nowrap" autoFocus>
                {state.cancelLabel}
              </button>
              <button onClick={handleConfirm}
                className={`px-5 py-2 rounded-xl text-sm font-sans font-semibold transition-colors whitespace-nowrap ${state.destructive ? 'bg-ug-status-red text-white hover:bg-ug-status-red/80' : 'bg-ug-gold text-ug-deepest hover:bg-ug-gold-bright'}`}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within ConfirmProvider');
  return confirm;
}
